import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, X, Ticket, AlertTriangle, Clock, CheckCircle,
  MessageSquare, User, Calendar, ChevronRight, Bug, HelpCircle, CreditCard, Lightbulb
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const typeLabels = {
  bug: { label: 'Bug technique', color: 'bg-red-100 text-red-700', icon: Bug },
  question: { label: 'Question', color: 'bg-blue-100 text-blue-700', icon: HelpCircle },
  billing: { label: 'Facturation', color: 'bg-yellow-100 text-yellow-700', icon: CreditCard },
  feature: { label: 'Demande fonctionnalité', color: 'bg-purple-100 text-purple-700', icon: Lightbulb },
  other: { label: 'Autre', color: 'bg-slate-100 text-slate-700', icon: Ticket }
};

const statusLabels = {
  new: { label: 'Nouveau', color: 'bg-blue-500' },
  in_progress: { label: 'En cours', color: 'bg-yellow-500' },
  waiting: { label: 'En attente', color: 'bg-purple-500' },
  resolved: { label: 'Résolu', color: 'bg-green-500' },
  closed: { label: 'Fermé', color: 'bg-slate-400' }
};

const priorityLabels = {
  urgent: { label: 'Urgent', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  normal: { label: 'Normal', color: 'text-slate-600', bg: 'bg-white border-slate-200' },
  low: { label: 'Faible', color: 'text-slate-400', bg: 'bg-slate-50 border-slate-100' }
};

export default function AdminTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ new: 0, in_progress: 0, waiting: 0, resolved: 0, urgent_open: 0, total_open: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newComment, setNewComment] = useState('');
  
  // For creating new ticket
  const [clients, setClients] = useState([]);
  const [newTicket, setNewTicket] = useState({
    ticket_type: 'other',
    priority: 'normal',
    subject: '',
    description: '',
    client_id: ''
  });

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchClients();
  }, [filterStatus, filterPriority]);

  const fetchTickets = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      let url = `${API_URL}/api/admin-crm/tickets`;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTickets(await response.json());
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/tickets/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setClients(await response.json());
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Sujet et description requis');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/tickets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTicket)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Ticket ${data.ticket_number} créé`);
        setShowModal(false);
        resetForm();
        fetchTickets();
        fetchStats();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/tickets/${ticketId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchTickets();
        fetchStats();
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(
        `${API_URL}/api/admin-crm/tickets/${selectedTicket.id}/comments?content=${encodeURIComponent(newComment)}&is_internal=false`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Commentaire ajouté');
        setNewComment('');
        // Refresh ticket details
        const ticketRes = await fetch(`${API_URL}/api/admin-crm/tickets/${selectedTicket.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (ticketRes.ok) {
          setSelectedTicket(await ticketRes.json());
        }
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setNewTicket({
      ticket_type: 'other',
      priority: 'normal',
      subject: '',
      description: '',
      client_id: ''
    });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTickets = tickets.filter(t => {
    const matchesSearch = t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         t.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         t.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Tickets Support</h1>
          <p className="text-rm-text-secondary">Gérez les demandes de tes clients entrepreneurs</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-rm-primary hover:bg-rm-primary/90" data-testid="new-support-ticket-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau ticket
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rm-text">{stats.total_open}</p>
              <p className="text-xs text-rm-text-secondary">Tickets ouverts</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.urgent_open}</p>
              <p className="text-xs text-rm-text-secondary">Urgents</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rm-text">{stats.in_progress}</p>
              <p className="text-xs text-rm-text-secondary">En cours</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rm-text">{stats.resolved}</p>
              <p className="text-xs text-rm-text-secondary">Résolus</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un ticket..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white border border-slate-200 rounded-lg"
        >
          <option value="">Tous les statuts</option>
          <option value="new">Nouveau</option>
          <option value="in_progress">En cours</option>
          <option value="waiting">En attente</option>
          <option value="resolved">Résolu</option>
          <option value="closed">Fermé</option>
        </select>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="px-4 py-2 bg-white border border-slate-200 rounded-lg"
        >
          <option value="">Toutes priorités</option>
          <option value="urgent">Urgent</option>
          <option value="normal">Normal</option>
          <option value="low">Faible</option>
        </select>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        {filteredTickets.length === 0 ? (
          <div className="text-center py-12">
            <Ticket className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-rm-text-secondary">Aucun ticket support</p>
            <Button onClick={() => setShowModal(true)} className="mt-4 bg-rm-primary">
              <Plus className="w-4 h-4 mr-2" />
              Créer un ticket
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredTickets.map((ticket) => {
              const TypeIcon = typeLabels[ticket.ticket_type]?.icon || Ticket;
              return (
                <div
                  key={ticket.id}
                  className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${priorityLabels[ticket.priority]?.bg}`}
                  onClick={() => setSelectedTicket(ticket)}
                  data-testid={`support-ticket-${ticket.ticket_number}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${typeLabels[ticket.ticket_type]?.color}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-rm-text-secondary">{ticket.ticket_number}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${typeLabels[ticket.ticket_type]?.color}`}>
                            {typeLabels[ticket.ticket_type]?.label}
                          </span>
                          {ticket.priority === 'urgent' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                              URGENT
                            </span>
                          )}
                        </div>
                        <h3 className="font-medium text-rm-text">{ticket.subject}</h3>
                        <div className="flex items-center gap-4 mt-2 text-xs text-rm-text-secondary">
                          {ticket.client_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {ticket.client_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(ticket.created_at)}
                          </span>
                          {ticket.comments?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {ticket.comments.length}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${statusLabels[ticket.status]?.color}`}></div>
                      <span className="text-xs text-rm-text-secondary">{statusLabels[ticket.status]?.label}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-rm-text">Nouveau ticket support</h2>
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateTicket} className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Type</label>
                    <select
                      value={newTicket.ticket_type}
                      onChange={(e) => setNewTicket({ ...newTicket, ticket_type: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    >
                      <option value="bug">Bug technique</option>
                      <option value="question">Question utilisation</option>
                      <option value="billing">Facturation</option>
                      <option value="feature">Demande fonctionnalité</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Priorité</label>
                    <select
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    >
                      <option value="urgent">Urgent</option>
                      <option value="normal">Normal</option>
                      <option value="low">Faible</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Client concerné</label>
                  <select
                    value={newTicket.client_id}
                    onChange={(e) => setNewTicket({ ...newTicket, client_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="">-- Sélectionner un client --</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name} - {c.company_name || c.email}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Sujet *</label>
                  <input
                    type="text"
                    required
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    placeholder="Résumé du problème"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Description *</label>
                  <textarea
                    required
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    rows={4}
                    placeholder="Décrivez le problème en détail..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
                  <Button type="submit" className="bg-rm-primary">Créer le ticket</Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ticket Detail Slide-over */}
      <AnimatePresence>
        {selectedTicket && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex justify-end"
            onClick={() => setSelectedTicket(null)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-slate-100 p-4 z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-mono text-rm-text-secondary">{selectedTicket.ticket_number}</span>
                    <h2 className="text-lg font-bold text-rm-text">{selectedTicket.subject}</h2>
                  </div>
                  <button onClick={() => setSelectedTicket(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-6">
                {/* Status & Priority */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-xs text-rm-text-secondary mb-1">Statut</label>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="new">Nouveau</option>
                      <option value="in_progress">En cours</option>
                      <option value="waiting">En attente</option>
                      <option value="resolved">Résolu</option>
                      <option value="closed">Fermé</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-rm-text-secondary mb-1">Priorité</label>
                    <div className={`px-3 py-2 rounded-lg text-sm ${priorityLabels[selectedTicket.priority]?.bg}`}>
                      {priorityLabels[selectedTicket.priority]?.label}
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div>
                    <span className="text-xs text-rm-text-secondary">Type</span>
                    <p className="text-sm font-medium">{typeLabels[selectedTicket.ticket_type]?.label}</p>
                  </div>
                  {selectedTicket.client_name && (
                    <div>
                      <span className="text-xs text-rm-text-secondary">Client</span>
                      <p className="text-sm font-medium">{selectedTicket.client_name}</p>
                      {selectedTicket.client_email && (
                        <p className="text-xs text-rm-text-secondary">{selectedTicket.client_email}</p>
                      )}
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-rm-text-secondary">Créé le</span>
                    <p className="text-sm">{formatDate(selectedTicket.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-rm-text-secondary">Créé par</span>
                    <p className="text-sm">{selectedTicket.created_by_name}</p>
                  </div>
                  {selectedTicket.assigned_to_name && (
                    <div>
                      <span className="text-xs text-rm-text-secondary">Assigné à</span>
                      <p className="text-sm font-medium">{selectedTicket.assigned_to_name}</p>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-sm font-semibold text-rm-text mb-2">Description</h3>
                  <p className="text-sm text-rm-text-secondary whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {/* Resolution */}
                {selectedTicket.resolution && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-green-800 mb-2">Résolution</h3>
                    <p className="text-sm text-green-700">{selectedTicket.resolution}</p>
                    <p className="text-xs text-green-600 mt-2">
                      Résolu par {selectedTicket.resolved_by} le {formatDate(selectedTicket.resolved_at)}
                    </p>
                  </div>
                )}

                {/* Comments */}
                <div>
                  <h3 className="text-sm font-semibold text-rm-text mb-3">
                    Commentaires ({selectedTicket.comments?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {selectedTicket.comments?.map((comment) => (
                      <div key={comment.id} className={`rounded-lg p-3 ${comment.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-slate-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-rm-text">
                            {comment.created_by_name}
                            {comment.is_internal && <span className="ml-2 text-yellow-600">(interne)</span>}
                          </span>
                          <span className="text-xs text-rm-text-secondary">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-sm text-rm-text-secondary">{comment.content}</p>
                      </div>
                    ))}
                  </div>

                  {/* Add comment */}
                  <div className="mt-4 flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Ajouter un commentaire..."
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                    />
                    <Button onClick={handleAddComment} size="sm" className="bg-rm-primary">
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
